import { useRef, useState } from 'react';
import Icon from './Icon.jsx';

// Swipe left to delete.
//
// The gesture reveals the action rather than performing it: dragging a card is
// far too easy to do by accident on a phone in one hand, and a plan you spent a
// week assembling should not be one clumsy thumb away from gone. So the swipe
// uncovers a red button, and deleting is a deliberate second tap on it.
//
// Vertical scrolling has to survive: the axis is decided once, on the first few
// pixels of movement, and a drag that starts downward is left entirely to the
// browser. touch-action: pan-y says the same thing to the compositor, so the
// list still scrolls at sixty frames while we are only ever handling sideways.

const REVEAL = 96;     // how much of the button shows when open
const TRIGGER = 42;    // how far you must pull for it to stay open

export default function SwipeToDelete({ onDelete, prompt, label = 'Elimina', children }) {
  const [dx, setDx] = useState(0);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const g = useRef(null);

  const settle = (isOpen) => { setOpen(isOpen); setDx(isOpen ? -REVEAL : 0); };

  const onTouchStart = (e) => {
    if (busy) return;
    const t = e.touches[0];
    g.current = { x: t.clientX, y: t.clientY, axis: null, base: open ? -REVEAL : 0 };
  };

  const onTouchMove = (e) => {
    if (!g.current) return;
    const t = e.touches[0];
    const mx = t.clientX - g.current.x;
    const my = t.clientY - g.current.y;

    if (!g.current.axis) {
      if (Math.abs(mx) < 6 && Math.abs(my) < 6) return;
      g.current.axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
      if (g.current.axis === 'x') setDragging(true);
    }
    if (g.current.axis !== 'x') return;

    // Rightward past closed does nothing; leftward stops a little past the
    // button so the card cannot be flung off its own row.
    setDx(Math.min(0, Math.max(-REVEAL - 16, g.current.base + mx)));
  };

  const onTouchEnd = () => {
    const axis = g.current?.axis;
    g.current = null;
    setDragging(false);
    if (axis !== 'x') return;
    settle(dx < -TRIGGER);
  };

  const remove = async () => {
    if (prompt && !window.confirm(prompt)) { settle(false); return; }
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      alert(err.message);
      setBusy(false);
      settle(false);
    }
  };

  return (
    <div className="swipe">
      <button className="swipe-del" disabled={busy} tabIndex={open ? 0 : -1}
              aria-hidden={!open} onClick={remove}>
        <Icon name="trash" size={17} />
        {busy ? 'Elimino…' : label}
      </button>

      <div className="swipe-track"
           style={{ transform: `translateX(${dx}px)`,
                    transition: dragging ? 'none' : undefined }}
           onTouchStart={onTouchStart} onTouchMove={onTouchMove}
           onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
           /* While the button is showing, the next tap anywhere on the card puts
              it away instead of doing whatever it would normally have done. */
           onClickCapture={open ? (e) => { e.preventDefault(); e.stopPropagation(); settle(false); } : undefined}>
        {children}
      </div>
    </div>
  );
}
